import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { ARTICLE_LANGUAGES } from '../enums';
import { resolveArticleLanguage } from '../helpers';
import useWhatsNewStore from '../store';

// NOTE :: A tip's title and short description ship in the app's translations. A super admin can
// replace them; the saved text wins, then English, then what shipped. The tip bubble, the help
// menu and the What's New cards all read them here, so they never disagree.
export default function useHelpTipTexts() {
  const { tipTexts } = storeToRefs(useWhatsNewStore());
  const { replaceTipText } = useWhatsNewStore();

  const { locale, t, te } = useI18n();

  const language = computed(() => resolveArticleLanguage(locale.value));

  // HELPERS
  function getSavedLangs(tipKey: string) {
    return tipTexts.value.find(({ key }) => key === tipKey)?.langs || {};
  }

  function findSavedText(tipKey: string, language: string, field: 'text' | 'title') {
    return getSavedLangs(tipKey)[language]?.[field] || '';
  }

  function findShippedText(tipKey: string, language: string, field: 'text' | 'title') {
    const translationKey = `helpTips.tips.${tipKey}.${field}`;

    return te(translationKey, language) ? t(translationKey, {}, { locale: language }) : '';
  }

  // NOTE :: Each language shows its own text first: what a super admin saved in it, then what ships
  // in it. The saved English comes last, so editing English never replaces a translation that exists.
  function getTipTitleForLanguage(tipKey: string, language: string, fallbackName = '') {
    return (
      findSavedText(tipKey, language, 'title') ||
      fallbackName ||
      findShippedText(tipKey, language, 'title') ||
      findSavedText(tipKey, ARTICLE_LANGUAGES.EN, 'title')
    );
  }

  function getTipTextForLanguage(tipKey: string, language: string) {
    return (
      findSavedText(tipKey, language, 'text') ||
      findShippedText(tipKey, language, 'text') ||
      findSavedText(tipKey, ARTICLE_LANGUAGES.EN, 'text')
    );
  }

  function getTipTitle(tipKey: string, fallbackName = '') {
    return getTipTitleForLanguage(tipKey, language.value, fallbackName);
  }

  function getTipText(tipKey: string) {
    return getTipTextForLanguage(tipKey, language.value);
  }

  return {
    getSavedLangs,
    getTipText,
    getTipTextForLanguage,
    getTipTitle,
    getTipTitleForLanguage,
    language,
    replaceTipText,
  };
}
